import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion, useScroll, useTransform } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import CountUp from 'react-countup';
import { ArrowRight, ArrowUpRight, Play, Zap, Shield, Leaf, Building2, ChevronRight, Film, Star, Lightbulb } from 'lucide-react';
import { trackCTAClick, trackPageView } from '../utils/analytics';
import WaterTestWall from '../components/WaterTestWall';
import StrengthScannerTeaser from '../components/StrengthScannerTeaser';
import SEOHead from '../components/SEOHead';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Stats data
const stats = [
  { value: 20, suffix: '+', label: 'Years of Excellence', icon: '⏱' },
  { value: 500, suffix: '+', label: 'Dealer Network', icon: '🤝' },
  { value: 3, suffix: '', label: 'States Covered', icon: '📍' },
  { value: 1000, suffix: '+', label: 'Projects Built', icon: '🏗' },
];

// Media Hub Teaser - links to the Media page
const MediaHubTeaser = () => {
  const [ref, inView] = useInView({ triggerOnce: true, threshold: 0.2 });

  return (
    <section ref={ref} className="py-16 md:py-20 bg-surface border-y border-white/5" data-testid="media-hub-teaser">
      <div className="container mx-auto px-4 md:px-8 max-w-6xl">
        <div className="flex flex-col md:flex-row items-center justify-between gap-8">
          <motion.div initial={{ opacity: 0, x: -20 }} animate={inView ? { opacity: 1, x: 0 } : {}}>
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-xl bg-red-500/10 flex items-center justify-center">
                <Film className="w-5 h-5 text-red-500" />
              </div>
              <span className="text-red-400 font-mono text-xs uppercase">Media Hub</span>
            </div>
            <h3 className="font-display text-2xl md:text-3xl font-bold text-white mb-2">See us in Action</h3>
            <p className="text-gray-400 text-sm max-w-md">Factory tours, brand films, aur behind-the-scenes content. Dekhiye hum har bag mein quality kaise laate hain.</p>
          </motion.div>
          <motion.div initial={{ opacity: 0, x: 20 }} animate={inView ? { opacity: 1, x: 0 } : {}} transition={{ delay: 0.2 }}>
            <Link to="/media" data-testid="media-hub-teaser-cta"
              className="group flex items-center gap-3 bg-red-500/10 hover:bg-red-500/20 border border-red-500/20 hover:border-red-500/30 rounded-2xl px-6 py-4 transition-all">
              <div className="w-12 h-12 rounded-full bg-red-500 flex items-center justify-center group-hover:scale-110 transition-transform">
                <Play className="w-5 h-5 text-white fill-white ml-0.5" />
              </div>
              <div>
                <span className="text-white font-display font-medium block">Videos Dekhein</span>
                <span className="text-gray-500 text-xs">7 videos available</span>
              </div>
              <ChevronRight className="w-5 h-5 text-gray-500 group-hover:text-white group-hover:translate-x-1 transition-all" />
            </Link>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

// Hero Section
const HeroSection = () => {
  const { scrollY } = useScroll();
  const opacity = useTransform(scrollY, [0, 400], [1, 0]);
  const scale = useTransform(scrollY, [0, 400], [1, 0.95]);
  const y = useTransform(scrollY, [0, 400], [0, 100]);

  return (
    <section className="relative min-h-[85vh] md:min-h-screen flex items-center overflow-hidden" data-testid="hero-section">
      {/* Animated Background */}
      <div className="absolute inset-0 bg-void">
        {/* Grid Pattern */}
        <div className="absolute inset-0 bg-grid opacity-40" />
        
        {/* Gradient Orbs */}
        <motion.div
          className="absolute top-1/4 -left-32 w-[500px] h-[500px] rounded-full"
          style={{ background: 'radial-gradient(circle, rgba(243,100,88,0.2) 0%, transparent 70%)' }}
          animate={{ scale: [1, 1.2, 1], x: [0, 50, 0] }}
          transition={{ duration: 8, repeat: Infinity, ease: 'easeInOut' }}
        />
        <motion.div
          className="absolute bottom-1/4 -right-32 w-[600px] h-[600px] rounded-full"
          style={{ background: 'radial-gradient(circle, rgba(59,130,246,0.15) 0%, transparent 70%)' }}
          animate={{ scale: [1.2, 1, 1.2], x: [0, -50, 0] }}
          transition={{ duration: 10, repeat: Infinity, ease: 'easeInOut', delay: 1 }}
        />
      </div>

      {/* Content */}
      <motion.div 
        style={{ opacity, scale, y }}
        className="relative z-10 container mx-auto px-4 md:px-8 max-w-7xl pt-8 md:pt-20"
      >
        <div className="max-w-4xl">
          {/* Badge */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="inline-flex items-center gap-2 bg-surface-light border border-white/10 px-4 py-2 rounded-full mb-8"
          >
            <span className="w-2 h-2 bg-coral rounded-full animate-pulse" />
            <span className="text-gray-400 text-sm font-body">Building India since 2004</span>
          </motion.div>

          {/* Headline */}
          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="font-display text-4xl sm:text-5xl md:text-7xl lg:text-8xl font-bold text-white leading-[0.95] mb-6"
          >
            Strength that{' '}
            <span className="text-gradient">builds</span>{' '}
            the future
          </motion.h1>
          
          <motion.p
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="text-gray-400 text-lg md:text-xl max-w-2xl mb-10 leading-relaxed"
          >
            Premium cement, AAC blocks & construction chemicals. Trusted by builders across Gujarat and Rajasthan for two decades.
          </motion.p>

          {/* CTAs */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.3 }}
            className="flex flex-col sm:flex-row gap-4"
          >
            <Link 
              to="/products"
              onClick={() => trackCTAClick('explore_products', 'hero')}
              data-testid="hero-cta-products"
              className="group flex items-center justify-center gap-2 bg-coral hover:bg-coral-dark text-white font-display font-medium px-8 py-4 rounded-full transition-all duration-300"
            >
              Explore Products 
              <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </Link>
            <Link 
              to="/distributors"
              onClick={() => trackCTAClick('become_distributor', 'hero')}
              data-testid="hero-cta-distributor"
              className="group flex items-center justify-center gap-2 border border-white/20 hover:border-white/40 hover:bg-white/5 text-white font-display font-medium px-8 py-4 rounded-full transition-all duration-300"
            >
              Become a Partner
              <ArrowUpRight className="w-5 h-5 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
            </Link>
          </motion.div>
        </div>

        {/* Scroll Indicator */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.5 }}
          className="absolute bottom-10 left-1/2 -translate-x-1/2"
        >
          <motion.div
            animate={{ y: [0, 10, 0] }}
            transition={{ repeat: Infinity, duration: 1.5 }}
            className="flex flex-col items-center gap-2"
          >
            <span className="text-gray-600 text-xs font-mono">SCROLL</span>
            <div className="w-px h-8 bg-gradient-to-b from-coral to-transparent" />
          </motion.div>
        </motion.div>
      </motion.div>
    </section>
  );
};

// Stats Section
const StatsSection = () => {
  const [ref, inView] = useInView({ triggerOnce: true, threshold: 0.3 });

  return (
    <section ref={ref} className="py-12 md:py-32 bg-surface relative" data-testid="stats-section">
      <div className="absolute inset-0 bg-grid opacity-20" />
      
      <div className="container mx-auto px-4 md:px-8 max-w-7xl relative z-10">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-8">
          {stats.map((stat, index) => (
            <motion.div
              key={stat.label}
              initial={{ opacity: 0, y: 30 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.5, delay: index * 0.1 }}
              className="text-center"
            >
              <div className="text-4xl md:text-5xl lg:text-6xl font-display font-bold text-white mb-2">
                {inView ? <CountUp end={stat.value} duration={2.5} /> : '0'}
                <span className="text-coral">{stat.suffix}</span>
              </div>
              <p className="text-gray-500 font-body text-sm md:text-base">
                {stat.label}
              </p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Products Showcase
const ProductsShowcase = ({ products }) => {
  const [ref, inView] = useInView({ triggerOnce: true, threshold: 0.1 });

  return (
    <section ref={ref} className="py-12 md:py-32 bg-void relative" data-testid="products-showcase">
      <div className="absolute inset-0 bg-grid opacity-20" />
      
      <div className="container mx-auto px-4 md:px-8 max-w-7xl relative z-10">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-16"
        >
          <div>
            <span className="text-coral font-mono text-sm mb-4 block">PRODUCTS</span>
            <h2 className="font-display text-3xl md:text-4xl lg:text-5xl font-bold text-white">
              Everything your<br />project needs
            </h2>
          </div>
          <Link 
            to="/products"
            className="group flex items-center gap-2 text-gray-400 hover:text-white font-display transition-colors"
          >
            View all products
            <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
          </Link>
        </motion.div>

        {/* Products Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {products.slice(0, 6).map((product, index) => (
            <motion.div
              key={product.id}
              initial={{ opacity: 0, y: 30 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ delay: index * 0.1 }}
            >
              <Link
                to={`/products/${product.id}`}
                data-testid={`product-card-${product.id}`}
                className="group block bg-surface border border-white/5 hover:border-white/20 p-6 rounded-2xl transition-all duration-300 h-full card-lift"
              >
                {/* Color indicator */}
                <div 
                  className="w-12 h-12 rounded-xl mb-6 flex items-center justify-center"
                  style={{ backgroundColor: `${product.color}20` }}
                >
                  <div 
                    className="w-4 h-4 rounded"
                    style={{ backgroundColor: product.color }}
                  />
                </div>
                
                <span className="text-xs font-mono text-gray-500 uppercase tracking-wider">
                  {product.category}
                </span>
                
                <h3 className="font-display text-xl font-semibold text-white mt-2 mb-3 group-hover:text-coral transition-colors">
                  {product.name}
                </h3>
                
                <p className="text-gray-500 text-sm mb-6 line-clamp-2">
                  {product.tagline}
                </p>
                
                <div className="flex items-center gap-2 text-coral text-sm font-display">
                  Learn more
                  <ChevronRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                </div>
              </Link>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Features Section
const FeaturesSection = () => {
  const [ref, inView] = useInView({ triggerOnce: true, threshold: 0.2 });
  
  const features = [
    { 
      icon: Shield, 
      title: 'Unmatched Quality', 
      desc: 'Every batch tested. Every delivery guaranteed.',
      color: '#f36458'
    },
    { 
      icon: Zap, 
      title: 'Fast Delivery', 
      desc: 'Pan-regional logistics for on-time supply.',
      color: '#3b82f6'
    },
    { 
      icon: Leaf, 
      title: 'Sustainable', 
      desc: 'Flyash utilization & eco-friendly products.',
      color: '#10b981'
    },
    { 
      icon: Building2, 
      title: '20+ Years Trust', 
      desc: 'Building India\'s infrastructure since 2004.',
      color: '#f59e0b'
    },
  ];

  return (
    <section ref={ref} className="py-12 md:py-32 bg-surface relative overflow-hidden">
      <div className="absolute inset-0 bg-grid opacity-20" />
      
      <div className="container mx-auto px-4 md:px-8 max-w-7xl relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          className="text-center mb-16"
        >
          <span className="text-coral font-mono text-sm mb-4 block">WHY CHOOSE US</span>
          <h2 className="font-display text-3xl md:text-4xl lg:text-5xl font-bold text-white">
            Built different.{' '}
            <span className="text-gray-500">Built better.</span>
          </h2>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {features.map((feature, index) => (
            <motion.div
              key={feature.title}
              initial={{ opacity: 0, y: 30 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ delay: index * 0.1 }}
              className="bg-void/50 border border-white/5 p-6 rounded-2xl text-center hover:border-white/20 transition-colors"
            >
              <div 
                className="w-14 h-14 rounded-xl mx-auto mb-4 flex items-center justify-center"
                style={{ backgroundColor: `${feature.color}15` }}
              >
                <feature.icon className="w-6 h-6" style={{ color: feature.color }} />
              </div>
              <h3 className="font-display text-lg font-semibold text-white mb-2">
                {feature.title}
              </h3>
              <p className="text-gray-500 text-sm">{feature.desc}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Testimonials Section
const TestimonialsSection = () => {
  const [ref, inView] = useInView({ triggerOnce: true, threshold: 0.2 });
  const [testimonials, setTestimonials] = useState([]);

  useEffect(() => {
    fetch(`${BACKEND_URL}/api/testimonials`).then(r => r.json()).then(setTestimonials).catch(() => {});
  }, []);

  return (
    <section ref={ref} className="py-12 md:py-24 bg-surface" data-testid="testimonials-section">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={inView ? { opacity: 1, y: 0 } : {}} className="text-center mb-12">
          <span className="text-coral font-mono text-xs tracking-[0.2em] uppercase">Testimonials</span>
          <h2 className="font-display text-2xl md:text-3xl font-bold text-white mt-3">Humari <span className="text-avi-gold">Quality</span> ke Gawaah</h2>
        </motion.div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {testimonials.slice(0, 6).map((t, idx) => (
            <motion.div key={t.id} initial={{ opacity: 0, y: 20 }} animate={inView ? { opacity: 1, y: 0 } : {}} transition={{ delay: idx * 0.1 }}
              className="bg-void border border-white/5 rounded-2xl p-6" data-testid={`testimonial-${t.id}`}>
              <div className="flex gap-1 mb-3">
                {[...Array(t.rating)].map((_, i) => <Star key={i} className="w-4 h-4 text-[#F5A623] fill-[#F5A623]" />)}
              </div>
              <p className="text-gray-300 text-sm leading-relaxed mb-4">"{t.text}"</p>
              <div className="border-t border-white/5 pt-4">
                <p className="text-white text-sm font-medium">{t.name}</p>
                <p className="text-gray-500 text-xs">{t.role} — {t.location}</p>
                <p className="text-[#E85A1C] text-xs mt-1">{t.project}</p>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Construction Tips Carousel
const constructionTips = [
  { title: "Cement Storage Tips", tip: "Cement bags ko zameen se 6 inch upar rakhein, moisture se bachayein. Puraana cement pehle use karein — FIFO method follow karein.", icon: "📦" },
  { title: "Monsoon Construction", tip: "Baarish ke mausam mein fresh concrete ko 24 ghante plastic sheet se dhakein. Waterproof admixture use karein aur curing properly karein.", icon: "🌧" },
  { title: "Water-Cement Ratio", tip: "M20 concrete ke liye 30 litre paani per 50kg bag cement use karein. Zyada paani = weak concrete. Workability ke liye superplasticizer use karein.", icon: "💧" },
  { title: "Curing is Important", tip: "Minimum 7 din curing zaruri hai — 28 din tak karein toh strength 30% zyada milegi. Pehle 3 din sabse critical hain.", icon: "⏰" },
  { title: "Quality Check at Site", tip: "Cement bag kholne pe smooth feel aaye, lumps na ho, aur uniform grey-green color ho — toh quality sahi hai.", icon: "✅" },
  { title: "Right Grade Selection", tip: "Slab ke liye OPC 53, walls ke liye OPC 43/PPC, marine structures ke liye PPC ya Composite cement use karein.", icon: "📋" },
];

const ConstructionTips = () => {
  const [ref, inView] = useInView({ triggerOnce: true, threshold: 0.2 });
  const [active, setActive] = useState(0);

  useEffect(() => {
    const timer = setInterval(() => setActive(p => (p + 1) % constructionTips.length), 5000);
    return () => clearInterval(timer);
  }, []);

  return (
    <section ref={ref} className="py-12 md:py-24 bg-void" data-testid="construction-tips">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={inView ? { opacity: 1, y: 0 } : {}} className="text-center mb-10">
          <span className="text-coral font-mono text-xs tracking-[0.2em] uppercase">Construction Tips</span>
          <h2 className="font-display text-2xl md:text-3xl font-bold text-white mt-3"><span className="text-avi-gold">Smart</span> Construction Tips</h2>
        </motion.div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {constructionTips.map((t, idx) => (
            <motion.div key={idx} initial={{ opacity: 0, y: 15 }} animate={inView ? { opacity: 1, y: 0 } : {}} transition={{ delay: idx * 0.08 }}
              className={`p-5 rounded-xl border transition-all cursor-pointer ${active === idx ? 'bg-[#E85A1C]/10 border-[#E85A1C]/30' : 'bg-surface border-white/5 hover:border-white/10'}`}
              onClick={() => setActive(idx)} data-testid={`tip-${idx}`}>
              <div className="flex items-start gap-3">
                <span className="text-xl">{t.icon}</span>
                <div>
                  <h3 className="text-white text-sm font-semibold mb-1">{t.title}</h3>
                  <p className="text-gray-400 text-xs leading-relaxed">{t.tip}</p>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
        <div className="text-center mt-8">
          <Link to="/faq" className="text-[#E85A1C] hover:text-[#F5A623] text-sm transition-colors" data-testid="tips-faq-link">
            More questions? Check our FAQ &rarr;
          </Link>
        </div>
      </div>
    </section>
  );
};

// Partner CTA
const PartnerCTA = () => {
  const [ref, inView] = useInView({ triggerOnce: true, threshold: 0.3 });

  return (
    <section ref={ref} className="py-12 md:py-32 bg-void relative overflow-hidden">
      {/* Gradient Background */}
      <div className="absolute inset-0">
        <div className="absolute inset-0 bg-gradient-to-br from-coral/10 via-transparent to-electric/10" />
        <div className="absolute inset-0 bg-grid opacity-30" />
      </div>

      <div className="container mx-auto px-4 md:px-8 max-w-7xl relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          className="bg-surface border border-white/10 rounded-3xl p-8 md:p-16 text-center"
        >
          <h2 className="font-display text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-6">
            Ready to grow your business?
          </h2>
          <p className="text-gray-400 text-lg max-w-2xl mx-auto mb-10">
            Join our network of 500+ successful dealers and distributors. 
            Competitive margins, strong brand recognition, and dedicated support.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link 
              to="/distributors"
              onClick={() => trackCTAClick('join_network', 'partner_cta')}
              data-testid="partner-cta-join"
              className="group flex items-center justify-center gap-2 bg-coral hover:bg-coral-dark text-white font-display font-medium px-8 py-4 rounded-full transition-all"
            >
              Join Our Network
              <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </Link>
            <Link 
              to="/contact"
              className="flex items-center justify-center gap-2 border border-white/20 hover:border-white/40 text-white font-display font-medium px-8 py-4 rounded-full transition-all"
            >
              Contact Sales
            </Link>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

// Main HomePage Component
const HomePage = () => {
  const [products, setProducts] = useState([]);

  useEffect(() => {
    trackPageView('/', 'Home');
    
    fetch(`${BACKEND_URL}/api/products`)
      .then(res => res.json())
      .then(data => setProducts(data))
      .catch(err => console.error('Error fetching products:', err));
  }, []);

  return (
    <div data-testid="home-page" className="bg-void">
      <SEOHead
        title="Premium Cement & Construction Materials"
        description="Avi Cement Limited — India's trusted manufacturer of premium cement (Lion, Hathi, Siddhee), AAC blocks (EFCON), and construction chemicals (Cemtite). 750+ dealers across Gujarat & Rajasthan. Building India since 2004."
        path="/"
      />
      <HeroSection />
      <WaterTestWall />
      <StatsSection />
      <ProductsShowcase products={products} />
      <MediaHubTeaser />
      <StrengthScannerTeaser />
      <FeaturesSection />
      <TestimonialsSection />
      <ConstructionTips />
      <PartnerCTA />
    </div>
  );
};

export default HomePage;
